import type { CreateSavedDestinationInput, SavedDestination, UpdateSavedDestinationInput } from "@solomon-apps/shared";
import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    createSavedDestination,
    deleteSavedDestination,
    fetchSavedDestinations,
    updateSavedDestination
} from "@/services/clientSavedDestinations";

import { useClientContextKey } from "./useClientContextKey";

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

interface UseSavedDestinationsReturn {
    destinations: SavedDestination[];
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
}

export function useSavedDestinations(): UseSavedDestinationsReturn {
    const contextKey = useClientContextKey();
    const { data, isLoading, error, refetch } = useQuery<SavedDestination[], Error>({
        queryKey: ["saved-destinations", contextKey],
        queryFn: fetchSavedDestinations,
        staleTime: 5 * 60 * 1000
    });

    return {
        destinations: data ?? [],
        isLoading,
        error,
        refetch: (): void => {
            void refetch();
        }
    };
}

// ---------------------------------------------------------------------------
// Mutations — return the raw `useMutation` result.
// ---------------------------------------------------------------------------

export function useCreateSavedDestination(): UseMutationResult<SavedDestination, Error, CreateSavedDestinationInput> {
    const queryClient = useQueryClient();
    return useMutation<SavedDestination, Error, CreateSavedDestinationInput>({
        mutationFn: createSavedDestination,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["saved-destinations"]
            });
        }
    });
}

export function useUpdateSavedDestination(): UseMutationResult<
    SavedDestination,
    Error,
    { uuid: string; data: UpdateSavedDestinationInput }
> {
    const queryClient = useQueryClient();
    return useMutation<SavedDestination, Error, { uuid: string; data: UpdateSavedDestinationInput }>({
        mutationFn: ({ uuid, data }) => updateSavedDestination(uuid, data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["saved-destinations"]
            });
        }
    });
}

export function useDeleteSavedDestination(): UseMutationResult<void, Error, string> {
    const queryClient = useQueryClient();
    return useMutation<void, Error, string>({
        mutationFn: deleteSavedDestination,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["saved-destinations"]
            });
        }
    });
}
